/**
 * Реестр разделов кабинета агента (DEV-1265, ADR-0042).
 *
 * Бэкенд отдаёт только ключи и порядок; что раздел называется «Мои клиенты» и
 * лежит по /clients — знание фронта, и здесь оно живёт в одном месте вместо
 * трёх (меню, роутер, гейт).
 *
 * Ключи в НИЖНЕМ регистре и совпадают с app_sections.key дословно. Верхний
 * регистр не матчится намеренно: DEV-1114 уже показал, чем кончается
 * расхождение регистра между слоями.
 *
 * ⚠️ Реестр перечисляет РАЗДЕЛЫ, а не пункты меню. Разделов семнадцать, в меню
 * видно девять: остальные открываются с дашборда и по прямым ссылкам. Меню —
 * вёрстка (AgentLayout), карта — доступ; смешивать их нельзя, иначе появление
 * ключа на сервере молча добавит пункт в интерфейс.
 */

export const SECTION_KEYS = [
  'agent_home',
  'agent_dashboard',
  'agent_clients',
  'agent_submit_client',
  'agent_deals',
  'agent_projects',
  'agent_presentations',
  'agent_inquiries',
  'agent_reward',
  'agent_commission',
  'agent_partnership',
  'agent_education',
  'agent_strategies',
  'agent_meeting',
  'agent_support',
  'agent_other',
  'agent_profile',
] as const

export type SectionKey = (typeof SECTION_KEYS)[number]

export interface SectionMeta {
  /** Маршрут раздела; совпадает с path в router/index.ts. */
  path: string
  /**
   * Ключ i18n для меню. Есть только у разделов, которые в меню показываются —
   * остальным подпись не нужна, их открывают с дашборда.
   */
  labelKey?: string
}

export const SECTION_REGISTRY: Record<SectionKey, SectionMeta> = {
  agent_home: { path: '/home' },
  agent_dashboard: { path: '/dashboard', labelKey: 'agents.cabinet.nav.dashboard' },
  agent_clients: { path: '/clients', labelKey: 'agents.cabinet.nav.clients' },
  agent_submit_client: { path: '/submit-client' },
  agent_deals: { path: '/deals', labelKey: 'agents.cabinet.nav.deals' },
  agent_projects: { path: '/projects', labelKey: 'agents.cabinet.nav.projects' },
  agent_presentations: { path: '/presentations', labelKey: 'agents.cabinet.nav.presentations' },
  agent_inquiries: { path: '/inquiries' },
  agent_reward: { path: '/reward', labelKey: 'agents.cabinet.nav.reward' },
  agent_commission: { path: '/commission' },
  agent_partnership: { path: '/partnership' },
  agent_education: { path: '/education', labelKey: 'agents.cabinet.nav.education' },
  agent_strategies: { path: '/strategies' },
  agent_meeting: { path: '/meeting' },
  agent_support: { path: '/support', labelKey: 'agents.cabinet.nav.support' },
  agent_other: { path: '/other' },
  agent_profile: { path: '/profile', labelKey: 'agents.cabinet.nav.profile' },
}

const KEY_SET = new Set<string>(SECTION_KEYS)

/**
 * isKnownSectionKey отсеивает ключи, которых фронт не знает.
 *
 * Раздел может появиться в реестре на сервере раньше, чем маршрут во фронте:
 * посев — это INSERT в миграции, а маршрут — релиз. Неизвестный ключ надо
 * молча пропустить, а не падать на нём.
 */
export const isKnownSectionKey = (key: string): key is SectionKey => KEY_SET.has(key)
